import { Flame, Clock3, Calendar, type LucideIcon } from 'lucide-react'
import type {
  EnrichedAction,
  SignalType,
} from '../../services/recommendations_api'
import { SIGNAL_CONFIG } from './recommendationConfig'

/**
 * Circle background and icon color for each urgency level.
 * Replaces the old risk_level-based RISK_STYLES — urgency now drives the circle color.
 */
export const URGENCY_CIRCLE_STYLES: Record<
  EnrichedAction['urgency'],
  { iconBg: string; iconColor: string }
> = {
  do_now: { iconBg: 'bg-error-bg', iconColor: 'text-error' },
  today: { iconBg: 'bg-yellow-50', iconColor: 'text-yellow-500' },
  this_week: { iconBg: 'bg-fb-green-50', iconColor: 'text-fb-dark-green' },
}

/**
 * Icon for each signal type. Derived from SIGNAL_CONFIG.
 */
export const SIGNAL_TYPE_ICONS: Record<SignalType, LucideIcon> =
  Object.fromEntries(
    Object.entries(SIGNAL_CONFIG).map(([k, v]) => [k, v.icon])
  ) as Record<SignalType, LucideIcon>

/**
 * Configuration for urgency level icons.
 */
export const URGENCY_ICONS = {
  do_now: Flame,
  today: Clock3,
  this_week: Calendar,
} as const

/**
 * Configuration for urgency level colors.
 */
export const URGENCY_COLORS = {
  do_now: 'text-error',
  today: 'text-yellow-500',
  this_week: 'text-fb-dark-green',
} as const

/**
 * Tooltip content for urgency levels.
 */
export const URGENCY_TOOLTIPS: Record<
  EnrichedAction['urgency'],
  { title: string; content: string }
> = {
  do_now: {
    title: 'Hot',
    content: 'Your highest-priority actions right now',
  },
  today: {
    title: 'Today',
    content: 'Not immediately urgent but still time-sensitive',
  },
  this_week: {
    title: 'This Week',
    content: 'Plan to complete within the next few days',
  },
}

/**
 * Number of items to show by default before requiring "Show More" button.
 */
export const DEFAULT_VISIBLE_ITEMS = 3

/**
 * Configuration for signal-based sections displayed in the UI.
 * Derived from SIGNAL_CONFIG — add new signal types there.
 */
export const SIGNAL_SECTIONS: Array<{
  key: SignalType
  title: string
  description: string
  Icon: LucideIcon
  iconColor: string
}> = (Object.keys(SIGNAL_CONFIG) as SignalType[]).map((key) => ({
  key,
  title: SIGNAL_CONFIG[key].title,
  description: SIGNAL_CONFIG[key].description,
  Icon: SIGNAL_CONFIG[key].icon,
  iconColor: 'text-fg-secondary',
}))

/**
 * Tooltip content for signal types. Derived from SIGNAL_CONFIG.
 */
export const SIGNAL_TOOLTIPS: Record<
  SignalType,
  { title: string; content: string }
> = Object.fromEntries(
  Object.entries(SIGNAL_CONFIG).map(([k, v]) => [
    k,
    { title: v.singularTitle, content: v.tooltip },
  ])
) as Record<SignalType, { title: string; content: string }>

/**
 * Icon colors for each signal type.
 */
export const SIGNAL_ICON_COLORS: Record<SignalType, string> =
  Object.fromEntries(
    Object.keys(SIGNAL_CONFIG).map((k) => [k, 'text-fg-secondary'])
  ) as Record<SignalType, string>

/**
 * Format a signal-specific label showing the days metric.
 * Delegates to the signal config's formatLabel function.
 */
export const formatSignalLabel = (
  signalType: SignalType,
  daysUnreplied: number | null,
  daysStale: number | null,
  daysInStage: number | null,
  currentStage: string | null,
  daysSinceEmail: number | null = null,
  daysOverdue: number | null = null
): string => {
  const config = SIGNAL_CONFIG[signalType]
  if (!config) return ''
  // Build a minimal EnrichedAction-like object for the formatter
  return config.formatLabel({
    days_unreplied: daysUnreplied,
    days_stale: daysStale,
    days_in_stage: daysInStage,
    days_since_email: daysSinceEmail,
    days_overdue: daysOverdue,
    current_stage: currentStage,
  } as EnrichedAction)
}
